import React, { useState, useMemo } from "react";

export default function ClientSortableTable({
  columns,
  data,
  renderRow,
  filterable = false,
  sortable = true,
  tableClass = "",
  innerTableClass = "adminTestList",
  caption,
  onCaptionClick,
}) {
  // Infer columns from data if not provided
  const inferredColumns = useMemo(() => {
    if (columns && columns.length > 0) return columns;
    if (!data || data.length === 0) return [];
    // Use keys from first row
    return Object.keys(data[0]).map((key) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      filterable: true,
      sortable: true,
    }));
  }, [columns, data]);

  // Multi-column sort/filter state
  const [sortOrder, setSortOrder] = useState([]); // [{key, direction, filterValue, filterEnabled}]
  // Helper: get unique values for filter dropdown, filtered by previous filters
  function getUniqueValuesForFilter(colKey, chipIdx) {
    if (!Array.isArray(data)) return [];
    let filtered = data;
    // Apply all previous enabled filters in sortOrder
    for (let i = 0; i < chipIdx; ++i) {
      const s = sortOrder[i];
      if (s.filterEnabled && s.filterValue) {
        filtered = filtered.filter(row => String(row[s.key]) === String(s.filterValue));
      }
    }
    // Special handling for testTypeName/testType
    if (colKey === "testTypeName") {
      return [...new Set(filtered.map(row => row["testTypeName"] ?? row["testType"]).filter(v => v !== undefined && v !== null))];
    }
    return [...new Set(filtered.map(row => row[colKey]).filter(v => v !== undefined && v !== null))];
  }

  function handleSort(colKey) {
    setSortOrder((prev) => {
      const idx = prev.findIndex(s => s.key === colKey);
      if (idx >= 0) {
        // Toggle direction
        const newOrder = [...prev];
        newOrder[idx].direction = newOrder[idx].direction === "asc" ? "desc" : "asc";
        return newOrder;
      } else {
        // Add new sort
        return [...prev, { key: colKey, direction: "asc", filterValue: null, filterEnabled: false }];
      }
    });
  }

  function clearSort(colKey) {
    setSortOrder(prev => prev.filter(s => s.key !== colKey));
  }

  function handleFilterChange(e, colKey) {
    const value = e.target.value;
    setSortOrder(prev => prev.map(s => s.key === colKey ? { ...s, filterValue: value } : s));
  }

  // Drag-and-drop for sort chip reordering
  function toggleFilterEnabled(colKey) {
    setSortOrder(prev => prev.map(s => s.key === colKey ? { ...s, filterEnabled: !s.filterEnabled, filterValue: !s.filterEnabled ? s.filterValue : null } : s));
  }
  function handleDragStart(e, colKey) {
    e.dataTransfer.setData("text/plain", colKey);
    e.currentTarget.classList.add("dragging");
  }
  function handleDragEnd(e) {
    e.currentTarget.classList.remove("dragging");
  }
  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }
  function handleDrop(e, targetColKey) {
    e.preventDefault();
    const draggedColKey = e.dataTransfer.getData("text/plain");
    if (draggedColKey !== targetColKey) {
      setSortOrder(prev => {
        const newOrder = [...prev];
        const draggedIdx = newOrder.findIndex(s => s.key === draggedColKey);
        const targetIdx = newOrder.findIndex(s => s.key === targetColKey);
        const [dragged] = newOrder.splice(draggedIdx, 1);
        newOrder.splice(targetIdx, 0, dragged);
        return newOrder;
      });
    }
  }

  // Multi-column filter/sort
  const filteredSortedData = useMemo(() => {
    let result = Array.isArray(data) ? [...data] : [];
    // Filtering
    sortOrder.forEach(s => {
      if (s.filterEnabled && s.filterValue) {
        result = result.filter(row => String(row[s.key]) === String(s.filterValue));
      }
    });
    // Multi-column sorting
    if (sortOrder.length > 0) {
      result.sort((a, b) => {
        for (let s of sortOrder) {
          const av = a[s.key], bv = b[s.key];
          if (av === bv) continue;
          if (av == null) return 1;
          if (bv == null) return -1;
          const cmp = String(av).localeCompare(String(bv));
          if (cmp !== 0) return s.direction === "asc" ? cmp : -cmp;
        }
        return 0;
      });
    }
    return result;
  }, [data, sortOrder]);

  // Simply remove any 'collapsed' class to ensure tables start expanded
  const finalTableClass = useMemo(() => {
    return tableClass;//.replace(/\s*collapsed\s*/g, ' ').trim();
  }, [tableClass]);

  return (
    <div className={`tableWrapper dimensionTable scrollable collapsable clientSortable clientFilterable ${finalTableClass}`}>
      <table className={innerTableClass || undefined}>
        {caption && (
          <caption onClick={onCaptionClick}>
            <span className="leftHeader">
              <span className="sortFilters" onClick={e => e.stopPropagation()}>
                {sortOrder.length > 0 && (
                  <>
                    <span className="sort-label">Sort: </span>
                    <span className="sort-info">
                      {sortOrder.map((s, idx) => {
                        const col = inferredColumns.find(c => c.key === s.key);
                        return (
                          <span
                            key={s.key}
                            className="sort-item"
                            draggable
                            onDragStart={e => { e.stopPropagation(); handleDragStart(e, s.key); }}
                            onDragEnd={e => { e.stopPropagation(); handleDragEnd(e); }}
                            onDragOver={e => { e.stopPropagation(); handleDragOver(e); }}
                            onDrop={e => { e.stopPropagation(); handleDrop(e, s.key); }}
                          >
                            <span
                              className="sort-item-content"
                              onClick={e => e.stopPropagation()}
                            >
                              {col?.label || s.key}
                              {col?.subLabel ? ` ${col.subLabel}` : ""}
                              <span className="sort-direction sortIcon" onClick={e => { e.stopPropagation(); handleSort(s.key); }}>
                                {s.direction === 'asc' ? '▲' : '▼'}
                              </span>
                            </span>
                            {col && col.filterable && Array.isArray(data) && data.length > 0 && (
                              <span
                                className={`filter-icon sortIcon${s.filterEnabled ? ' active-filter' : ''}`}
                                onClick={e => { e.stopPropagation(); toggleFilterEnabled(s.key); }}
                                title={s.filterEnabled ? 'Disable filtering' : 'Enable filtering'}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                              </span>
                            )}
                            {col && col.filterable && s.filterEnabled && Array.isArray(data) && data.length > 0 && (
                              <select
                                className="filter-select"
                                value={s.filterValue || ''}
                                onChange={e => { e.stopPropagation(); handleFilterChange(e, s.key); }}
                              >
                                <option value="">All</option>
                                {getUniqueValuesForFilter(s.key, idx).map((v, i) => (
                                  <option key={i} value={v}>{v}</option>
                                ))}
                              </select>
                            )}
                            <span
                              className="remove-sort sortIcon"
                              onClick={e => { e.stopPropagation(); clearSort(s.key); }}
                              title="Remove this sort criterion"
                            >X</span>
                          </span>
                        );
                      })}
                    </span>
                  </>
                )}
              </span>
            </span>
                      {caption}
          </caption>
        )}
        <thead>
          <tr>
            {inferredColumns.map((col) => (
              <th
                key={col.key}
                className={sortable && col.sortable ? "sortableHeader" : undefined}
                onClick={sortable && col.sortable ? () => handleSort(col.key) : undefined}
              >
                <span className="columnTitle">
                  <span className="columnTitleMain">{col.label}</span>
                  <span className="columnTitleSub">{col.subLabel || "\u00a0"}</span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredSortedData.map((row, idx) => renderRow(row, idx))}
        </tbody>
      </table>
    </div>
  );
}

